/**
 * App-side wiring for `@heidi/workspace/data` (Wave 2 · W, ARCFE-801).
 *
 * Packages are variant-agnostic and store-free (G1/F5): this module is where
 * the app injects the REACTIVE auth token (a hook — injected as a hook so the
 * entitlements query re-keys on token refresh; sourced from the
 * already-store-coupled `useIsAuthenticated` module per the Rematch
 * containment ratchet) and coding's SCC access-context callbacks (inverting
 * what was a workspace → coding import). Transport is NOT injected here —
 * `data/api` reads the `@heidi/api/client` registry, which `scribeHTTP.ts`
 * populates at factory time (E5, ARCFE-805); the side-effect import below
 * guarantees that ordering. Imported at module scope in `_app.tsx` right
 * after `@/composition/wiring/runtime-wiring`, before any feature module renders a
 * workspace hook.
 *
 * It is also where the current-user fetch's four app effects live, now that
 * the fetch owner itself is behind the door. They are bound HERE rather than
 * at the delegate for the reason `onTeamDetailsFetchError` already records: a
 * `queryFn` belongs to the QUERY, not to an observer, so a per-caller port
 * fires or does not fire by mount order.
 *
 * Ruling 65 adds the identity and organisation reads to it. An ACTION crosses
 * as the app delegate itself, reactions included; a VALUE crosses as a
 * resolved value or a hook returning one, and each member below names the app
 * expression it mirrors.
 */
// Side-effect: builds the scribe clients AND sets the @heidi/api registry.
import '@/utils/scribeHTTP';

import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  hydrateIdentifySpeakersUserDefaultFromSessionSettings,
  identifySpeakersUserDefaultAtom,
  mergeIdentifySpeakersIntoUserCache,
  type IdentifySpeakersSettingsHost,
} from '@/container/ScribeTabs/TabPane/TranscriptTabPane/TranscriptContainer/transcript-settings/identify-speakers-default';
import {
  isDictationLanguageSettingsEnabled,
  isDictationOutputLanguageEnabled,
  useMultiWorkspaceSelectionGate,
} from '@/features/flags';
import {
  UPDATE_PROFILE,
  useMutateUpdateSettings,
} from '@/hooks/mutation/useMutateUpdateSettings';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { invalidateChats } from '@/hooks/query/useQueryChats';
import { invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import { useAuthToken } from '@/hooks/useIsAuthenticated';
import { migrateChatsFromLocalStorage } from '@/hooks/useMigrateChatsFromLocalStorage';
import { isModalClosed } from '@/lib/modalClosedRules';
import type { ErrorResponse, ModalView } from '@/types';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  getTempUserId,
  LAST_MIGRATED_CHAT_ID_KEY,
  setTempUserId,
} from '@/utils/tempUser';
// The state door, not the `@/lib/auth` barrel: it pulls in nothing but jotai,
// while the barrel reaches the token cache and would risk an import cycle.
import { authTransientFailureAtom } from '@heidi/auth/state';
import { ensureSccAccessContext } from '@heidi/coding/data';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { type ModalId } from '@heidi/schema/ml-scribe';
import {
  ENTITLEMENT,
  GET_USER,
  type UserEntitlementView,
} from '@heidi/workspace/contracts';
import {
  configureWorkspaceData,
  updateUser,
  type CurrentUserFetchedContext,
} from '@heidi/workspace/data';
import i18next from 'i18next';
import { getDefaultStore, useAtomValue } from 'jotai';
import Router, { useRouter } from 'next/router';

/**
 * Stable E2E selector for the post-consume ai_template quota toast — the
 * literal `packages/e2e-utils/src/page-objects/billing/quota.page.ts` reaches
 * for with `getByTestId`.
 *
 * It is declared beside the toast rather than left at
 * `src/hooks/query/useQueryUser.ts`, because nothing imported it from there
 * and a re-export would have made this module an importer of a file the
 * identity-projection family claims — one new ratchet pair bought for a
 * constant with no readers. The selector string is byte-identical.
 */
export const AI_TEMPLATE_QUOTA_TOAST_TEST_ID = 'quota-toast';

/**
 * The four effects the current-user `queryFn` used to run inline, in the one
 * order they ran in: three quota toasts, the seen-modal sync, the temp-user
 * clear, then the chat migration.
 *
 * TWO MECHANISMS CHANGED SHAPE AND NEITHER CHANGES A VALUE, so both are stated
 * rather than left for a reader to spot:
 *
 * - Copy came from `useTranslation()`'s bound `t`; it now comes from the bare
 *   `i18next.t`. Same singleton, same default namespace — `@/utils/translations`
 *   says so in as many words ("the non-React accessor delegates to the same
 *   singleton bare `i18next.t(...)`"), and `src/utils/reactQueryClient.ts`, the
 *   app's own toast subscriber, already formats this way. A toast is imperative,
 *   so there is no re-render subscription to lose.
 * - The seen-modal write came from `useSetAtom(seenModalIdsAtom)`; it now goes
 *   through `getDefaultStore()`. The app mounts no jotai `Provider` outside two
 *   dev pages and a story, so `useSetAtom` already resolved to the default
 *   store — the same instance either way, and the same pattern
 *   `scribeHttpErrorHandler`, `capture-wiring` and `getAuthTokenForHttpClient`
 *   use. `workspace-data-wiring.test.ts` pins that the atom a component reads
 *   is the atom this writes.
 */
const handleCurrentUserFetched = ({
  user: data,
  previousUser: previousData,
}: CurrentUserFetchedContext) => {
  if (
    previousData?.quota?.ai_template &&
    previousData?.quota.ai_template > data?.quota?.ai_template
  ) {
    toast({
      'data-testid': AI_TEMPLATE_QUOTA_TOAST_TEST_ID,
      tone: 'pro',
      title: i18next.t('account.normalDocumentActionsRemaining', {
        count: data.quota.ai_template,
      }),
    });
  }
  if (previousData?.quota?.ask_ai && previousData?.quota?.ask_ai > data?.quota?.ask_ai) {
    toast({
      tone: 'pro',
      title:
        data.quota.ask_ai === 0
          ? i18next.t('quota.askHeidiExhausted')
          : i18next.t('account.askHeidiActionsRemaining', {
              count: data.quota.ask_ai,
            }),
    });
  }
  if (
    previousData?.quota?.evidence_patient_or_session_linked &&
    previousData?.quota?.evidence_patient_or_session_linked >
      data?.quota?.evidence_patient_or_session_linked
  ) {
    toast({
      tone: 'pro',
      title:
        data.quota.evidence_patient_or_session_linked === 0
          ? i18next.t('quota.linkingExhausted')
          : i18next.t('account.linkingActionsRemaining', {
              count: data.quota.evidence_patient_or_session_linked,
            }),
    });
  }

  getDefaultStore().set(seenModalIdsAtom, (prev) => {
    const backendIds = data.seen_modals
      ? new Set<ModalId>(
          (Object.entries(data.seen_modals) as Array<[ModalId, ModalView]>)
            .filter(([id, view]) => isModalClosed(id, view))
            .map(([id]) => id)
        )
      : new Set<ModalId>();
    if (!prev) return backendIds;
    return new Set([...prev, ...backendIds]);
  });

  // Always clear temp user ID on successful authentication to prevent
  // authenticated users from being incorrectly treated as anonymous
  const tempUserId = getTempUserId();
  if (tempUserId) {
    setTempUserId(null);
  }

  // Handle chat migration after anonymous user signs up/logs in
  if (data.chats_migrated_count && data.chats_migrated_count > 0) {
    void invalidateChats();
    if (data.last_migrated_chat_id) {
      sessionStorage.setItem(LAST_MIGRATED_CHAT_ID_KEY, data.last_migrated_chat_id);
    }
  } else if (!data.chats_migrated_count && !!tempUserId) {
    // Backend did not migrate; run localStorage migration if user had unauthenticated chats
    void migrateChatsFromLocalStorage();
  }

  void hydrateIdentifySpeakersUserDefaultFromSessionSettings(
    data?.session_settings?.identify_speakers,
    {
      isUpdatingSettings:
        reactQueryClient.isMutating({ mutationKey: [UPDATE_PROFILE] }) > 0,
      persistFalse: async () => {
        // Yield so a same-tick settings PATCH can win before leftover persist.
        await Promise.resolve();
        if (reactQueryClient.isMutating({ mutationKey: [UPDATE_PROFILE] }) > 0) {
          return false;
        }
        if (getDefaultStore().get(identifySpeakersUserDefaultAtom) !== false) {
          return;
        }
        try {
          await updateUser({ session_settings: { identify_speakers: false } });
        } catch (error) {
          logger.warn('[DIARISATION] leftover identify-speakers migrate failed', {
            phase: 'persist_false',
            error: sanitizeError(error),
          });
          throw error;
        }
        // Leftover persist is untracked updateUser. If the user toggled on
        // while this PATCH was in flight, restore on so last-write is not off.
        if (getDefaultStore().get(identifySpeakersUserDefaultAtom) !== false) {
          try {
            await updateUser({ session_settings: { identify_speakers: true } });
          } catch (error) {
            logger.warn('[DIARISATION] leftover identify-speakers migrate failed', {
              phase: 'compensate_true',
              error: sanitizeError(error),
            });
          }
          // Compensate can settle after a later OFF (or ON onError revert).
          // Do not merge GET cache on or spend leftover over a false atom.
          if (getDefaultStore().get(identifySpeakersUserDefaultAtom) === false) {
            return false;
          }
          reactQueryClient.setQueryData([GET_USER], (oldData) =>
            mergeIdentifySpeakersIntoUserCache(
              oldData as IdentifySpeakersSettingsHost | undefined,
              true
            )
          );
        }
      },
    }
  );
};

/**
 * `useAtomValue(selectedReviewMemberAtom)`, as a named hook so the rules of
 * hooks apply to it where an inline object member would read as a callback.
 */
const useSelectedReviewMemberId = () => useAtomValue(selectedReviewMemberAtom);

/**
 * `useAtomValue(authTransientFailureAtom)`, as a named hook for the same
 * reason: an inline object member reads as a callback to the rules of hooks.
 */
const useIsTransientAuthFailure = () => useAtomValue(authTransientFailureAtom);

/**
 * `useRouter().pathname.startsWith('/join-team')`, as a named hook for the
 * same reason. The status hook holds a fresh multi-workspace login on the
 * selector everywhere EXCEPT here, where the accept flow switches the
 * workspace itself — the same read `AuthWrapper` makes for the boot writer.
 */
const useIsJoinTeamRoute = () => useRouter()?.pathname?.startsWith('/join-team') ?? false;

configureWorkspaceData({
  useAuthToken,
  useIsTransientAuthFailure,
  // The post-login selector's flag gate, composed app-side because it reads
  // provider readiness (env) — see its docstring in `@/features/flags`.
  useMultiWorkspaceSelectionGate,
  useIsJoinTeamRoute,
  queryClient: reactQueryClient,
  // The team-details fetch toast (ARCFE-921), byte-identical to the copy the
  // two collapsed delegates carried. Bound here rather than per caller because
  // the port fires from inside a `queryFn`, which belongs to the query and not
  // to an observer — see `packages/workspace/src/data/query/useQueryTeamDetails.test.tsx`.
  onTeamDetailsFetchError: (error) => {
    // Logged unconditionally: the package's catch swallows this error, so
    // without this line a suppressed toast would leave no trace at all.
    //
    // Projected through `sanitizeError` first: error-level logs are forwarded
    // to Datadog with their extra args intact, and pino's redaction runs only
    // on the local sink, so handing over the raw axios error would export the
    // request URL, the Authorization header and the response body. Status and
    // code are the diagnostic fields the logger's PHI contract allows.
    logger.error('Team details request failed', sanitizeError(error));
    if (shouldSuppressDesktopOfflineNetworkErrorToast(error)) return;

    // A transport failure has no `response`, so the old `errorData.detail.msg`
    // threw a TypeError out of the port rather than toasting. Optional
    // throughout; the description is simply absent when the backend sent no
    // detail.
    const errorData = error.response?.data as ErrorResponse | undefined;
    toast({
      tone: 'destructive',
      title: 'Backend Error',
      description: errorData?.detail?.msg,
    });
  },
  onCurrentUserFetched: handleCurrentUserFetched,
  // The destructive transport toast, byte-identical: it fired for an axios
  // error carrying a response that is not the org-access refusal, and read the
  // error's own message.
  onCurrentUserFetchError: (error) => {
    toast({
      tone: 'destructive',
      title: i18next.t('common.error'),
      description: error.message,
    });
  },
  // The hard redirect for a user the org's SSO group blocks. `Router.pathname`
  // is the same value `useRouter().pathname` read — one Next router singleton
  // backs both — and `window.location.replace` is deliberate: the legacy
  // comment records that it exists to cancel pending Next navigations, so it
  // must not become a soft push.
  onOrgAccessRequired: () => {
    if (Router.pathname !== '/organization-access-required') {
      window.location.replace('/organization-access-required');
    }
  },
  // MR-WORKSPACE-SCC-INVERSION (ARCFE-986): the five-callback `sccAccess`
  // injection is gone. Workspace awaits this domain-neutral reaction after
  // every entitlements fetch; the APP decides what reacts — today, coding's
  // ensure command (which never rejects, per the port contract). This is the
  // "app integration sequences the Coding-owned command" half of program 1.5.
  onEntitlementsResolved: (entitlements) =>
    ensureSccAccessContext({
      coderSeatEnabled:
        (entitlements.get(ENTITLEMENT.CODER_SEAT) as UserEntitlementView | undefined)
          ?.enabled === true,
    }),
  // Ruling 65. The four DELEGATES below cross by reference, never wrapped: a
  // wrapper would be a second binding to keep in step, and the ban's own
  // falsifier turns on a forward being reference-identical.
  getDictationLanguageFlags: () => ({
    languageSettingsEnabled: isDictationLanguageSettingsEnabled(),
    outputLanguageEnabled: isDictationOutputLanguageEnabled(),
  }),
  useSelectedReviewMemberId,
  useUpdateUser: useMutateUpdateUser,
  useUpdateUserSettings: useMutateUpdateSettings,
  useOrganizationInfo: useCombinedOrganizationInfo,
  invalidateCurrentUser,
});
